import type { Logger } from '@vp/ubik-logging'

import type { Ccv2Cart } from '../types/Ccv2Cart'
import { REQUESTOR } from '../utils/constants'

const baseUrl = 'https://carts.checkout.cimpress.io'
const TIMEOUT_MS = 10000

/**
 * Reads a shopper's cart from the CCv2 Cart API (`getCartByType`).
 *
 * Auth: the cart service grants shopper access when the request carries the
 * shopper's own bearer token and its principal matches `shopperId` (the
 * "shopper shortcut" in TrustCheckTokenValidator), so no `x-session-id` header
 * is required for this flow — only the existing `Authorization` header.
 */
export const getCart = async (accountId: string, shopperId: string, cartTypeId: string, authorizationHeader: string, logger: Logger): Promise<Ccv2Cart> => {
  const startTime = Date.now()
  const url = `${baseUrl}/v0/accounts/${accountId}/shoppers/${shopperId}/cartTypes/${cartTypeId}?requestor=${REQUESTOR}`

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      headers: {
        Authorization: authorizationHeader,
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`Request to ${url} failed with status: ${response.status}`)
    }

    const data = (await response.json()) as Ccv2Cart
    return data
  } catch (error) {
    clearTimeout(timeoutId)
    const duration = Date.now() - startTime

    if (error instanceof Error && error.name === 'AbortError') {
      logger.error('CCv2 cart request timed out', error, { url, accountId, cartTypeId, duration, timeout: TIMEOUT_MS })
    } else {
      logger.error('Failed to fetch cart from CCv2 Cart API', error as Error, { url, accountId, cartTypeId, duration })
    }

    throw error
  }
}
